import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import BaseDialog from './BaseDialog';
import GoogleMapsAutocomplete from './GoogleMapsAutocomplete';
import { customerService, Customer, CreateCustomerDto, CustomerSource } from '../../services/customerService';
import { Business } from '../../services/businessService';

interface CustomerFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (customer: Customer) => void;
  business: Business | null;
  title?: string;
}

const CustomerFormDialog: React.FC<CustomerFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  business,
  title = 'Create Customer'
}) => {
  const [formData, setFormData] = useState<CreateCustomerDto>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    country: '',
    source: CustomerSource.Other
  });
  
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addressKey, setAddressKey] = useState(0);

  // Update address key when business data changes to force re-render
  useEffect(() => {
    if (business) {
      setAddressKey(prev => prev + 1);
    }
  }, [business?.defaultCountry, business?.defaultCity]);

  // Validation functions
  const validateFirstName = (value: string): string | null => {
    if (!value.trim()) {
      return 'First name is required';
    }
    if (value.length > 100) {
      return 'First name cannot be longer than 100 characters';
    }
    return null;
  };

  const validateLastName = (value: string): string | null => {
    if (value && value.length > 100) {
      return 'Last name cannot be longer than 100 characters';
    }
    return null;
  };

  const validateEmail = (value: string): string | null => {
    if (value && value.trim()) {
      if (!/\S+@\S+\.\S+/.test(value)) {
        return 'Email is invalid';
      }
      if (value.length > 256) {
        return 'Email cannot be longer than 256 characters';
      }
    }
    return null;
  };

  const validatePhoneNumber = (value: string): string | null => {
    if (value && value.length > 20) {
      return 'Phone number cannot be longer than 20 characters';
    }
    return null;
  };

  const validateSource = (value: CustomerSource): string | null => {
    if (!value) {
      return 'Source is required';
    }
    return null;
  };

  const validateField = (field: string, value: string | CustomerSource): string | null => {
    switch (field) {
      case 'firstName':
        return validateFirstName(value as string);
      case 'lastName':
        return validateLastName(value as string);
      case 'email':
        return validateEmail(value as string);
      case 'phoneNumber':
        return validatePhoneNumber(value as string);
      case 'source':
        return validateSource(value as CustomerSource);
      default:
        return null;
    }
  };

  const handleFieldBlur = (field: string) => {
    const value = formData[field as keyof CreateCustomerDto] || '';
    const error = validateField(field, value);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error || ''
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    const firstNameError = validateFirstName(formData.firstName);
    if (firstNameError) errors.firstName = firstNameError;
    
    const lastNameError = validateLastName(formData.lastName || '');
    if (lastNameError) errors.lastName = lastNameError;
    
    const emailError = validateEmail(formData.email || '');
    if (emailError) errors.email = emailError;
    
    const phoneError = validatePhoneNumber(formData.phoneNumber || '');
    if (phoneError) errors.phoneNumber = phoneError;
    
    const sourceError = validateSource(formData.source);
    if (sourceError) errors.source = sourceError;
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddressChange = (address: string, latitude?: number, longitude?: number) => {
    setFormData(prev => ({
      ...prev,
      address
    }));
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      address: '',
      city: '',
      country: '',
      source: CustomerSource.Other
    });
    setValidationErrors({});
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      // Clean up form data - convert empty strings to undefined for optional fields
      const cleanedFormData = {
        ...formData,
        lastName: formData.lastName || undefined,
        email: formData.email || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        address: formData.address || undefined,
        city: formData.city || undefined,
        country: formData.country || undefined,
        secondaryContactName: formData.secondaryContactName || undefined,
        secondaryContactPhone: formData.secondaryContactPhone || undefined,
        secondaryContactEmail: formData.secondaryContactEmail || undefined,
        preferredCommunicationMethod: formData.preferredCommunicationMethod || undefined,
        notes: formData.notes || undefined
      };
      
      const customer = await customerService.createCustomer(cleanedFormData);
      setSuccess('Customer created');
      setError(null);
      
      // Call onSuccess callback with the created customer
      setTimeout(() => {
        resetForm();
        onSuccess(customer);
      }, 500);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        // Handle validation errors
        const serverValidationErrors = err.response.data.errors;
        const errorMessages = Object.entries(serverValidationErrors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`);
        
        if (errorMessages.length === 1) {
          setError(errorMessages[0]);
        } else {
          setError('Please fix the following errors:');
        }
      } else {
        setError(err.message || 'Failed to create customer');
      }
      setSuccess(null);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <BaseDialog 
      open={open} 
      onClose={handleClose}
      title={title}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
        }
      }}
      contentSx={{ pb: 4 }}
      actions={
        <>
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">Create Customer</Button>
        </>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
          {error.includes('Please fix the following errors:') ? (
            <div>
              <div>{error}</div>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {Object.entries(validationErrors).map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
              </ul>
            </div>
          ) : (
            error
          )}
        </Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{success}</Alert>}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            onBlur={() => handleFieldBlur('firstName')}
            error={!!validationErrors.firstName}
            helperText={validationErrors.firstName}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
            onBlur={() => handleFieldBlur('lastName')}
            error={!!validationErrors.lastName}
            helperText={validationErrors.lastName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            onBlur={() => handleFieldBlur('email')}
            error={!!validationErrors.email}
            helperText={validationErrors.email}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
            onBlur={() => handleFieldBlur('phoneNumber')}
            error={!!validationErrors.phoneNumber}
            helperText={validationErrors.phoneNumber}
          />
        </Grid>
        <Grid item xs={12}>
          <GoogleMapsAutocomplete
            key={`address-create-${addressKey}`}
            value={formData.address || ''}
            onChange={handleAddressChange}
            label="Address"
            defaultBounds={{
              city: business?.defaultCity,
              country: business?.defaultCountry
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="City"
            value={formData.city}
            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <FormControl fullWidth>
            <InputLabel>Source</InputLabel>
            <Select
              value={formData.source}
              onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value as CustomerSource }))}
              onBlur={() => handleFieldBlur('source')}
              label="Source"
              error={!!validationErrors.source}
            >
              {Object.values(CustomerSource).filter(v => typeof v === 'number').map((source) => (
                <MenuItem key={source} value={source}>
                  {customerService.getSourceDisplayName(source as CustomerSource)}
                </MenuItem>
              ))}
            </Select>
            {validationErrors.source && (
              <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                {validationErrors.source}
              </div>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          />
        </Grid>
      </Grid>
    </BaseDialog>
  );
};

export default CustomerFormDialog;
